import type { ComponentType, ReactNode } from "react";
import { useMatches } from "react-router";
import { useI18n } from "../i18n/index.js";
import { Brand, CenteredPage, KeyValue, SettingsRow, Skeleton, Table } from "../ui/index.js";

/**
 * Loading is drawn as the page it will become: the same header, cards, rows and tables,
 * with a bar wherever data will go. What does not depend on the data — titles, column
 * headings, field labels — is drawn for real. Every page's skeleton is built from these.
 */

// A few bar lengths, so rows of placeholders do not read as one solid block.
const LENGTHS = ["72%", "54%", "64%", "46%", "60%"];
export const barLength = (i: number) => LENGTHS[i % LENGTHS.length]!;

export const times = (n: number) => Array.from({ length: n }, (_, i) => i);

/** Wraps a skeleton so a screen reader hears that the page is loading, not a page of blanks. */
export function Loading({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  return (
    <>
      <span className="dt-sr-only dt-loading-status" role="status">
        {t("common.loading")}
      </span>
      {children}
    </>
  );
}

/** Paragraphs of text. */
export function Lines({ count = 2 }: { count?: number }) {
  return times(count).map((i) => (
    <p key={i}>
      <Skeleton width={barLength(i)} />
    </p>
  ));
}

/** A table: its real `head`, and `count` rows whose cells `cells` draws. */
export function TableSkeleton({ head, cells, count = 5 }: { head?: ReactNode; cells: (row: number) => ReactNode[]; count?: number }) {
  return (
    <Table>
      {head}
      <tbody>
        {times(count).map((row) => (
          <tr key={row}>
            {cells(row).map((cell, i) => (
              <td key={i}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** Settings rows: a label, maybe a description, and `control` on the right. */
export function SettingsRowsSkeleton({ count = 3, description = true, control }: { count?: number; description?: boolean; control?: ReactNode }) {
  return times(count).map((i) => (
    <SettingsRow key={i} label={<Skeleton width={i % 2 ? "28%" : "36%"} />} description={description ? <Skeleton width={barLength(i)} /> : undefined}>
      {control}
    </SettingsRow>
  ));
}

/** Rows of a `dt-drafts` list: a title and a line under it, `actions` on the right. */
export function DraftRowsSkeleton({ count = 3, actions }: { count?: number; actions?: ReactNode }) {
  return (
    <ul className="dt-drafts">
      {times(count).map((i) => (
        <li key={i} className="dt-draft">
          <div className="dt-stack dt-gap-1">
            <Skeleton width={barLength(i)} />
            <Skeleton width={barLength(i + 2)} />
          </div>
          {actions && <span className="dt-inline dt-gap-2">{actions}</span>}
        </li>
      ))}
    </ul>
  );
}

/** A key–value list whose keys are known. */
export function KeyValueSkeleton({ keys }: { keys: string[] }) {
  return <KeyValue items={keys.map((key, i) => ({ key, value: <Skeleton width={barLength(i)} /> }))} />;
}

/** A form field: its real label and the box it will be. */
export function FieldSkeleton({ label, multiline = false }: { label: ReactNode; multiline?: boolean }) {
  return (
    <div className="dt-field">
      <span className="dt-field__label">{label}</span>
      <Skeleton shape="input" {...(multiline ? { height: 88 } : {})} />
    </div>
  );
}

/** A page on its own in the middle of the screen (sign-in, consent): the brand, a heading and what `children` draws. */
export function CenteredSkeleton({ width, children }: { width?: number; children: ReactNode }) {
  return (
    <CenteredPage {...(width ? { width } : {})}>
      <Loading>
        <div className="dt-centered__head">
          <Brand size="lg" />
          <div>
            <h1>
              <Skeleton width={220} />
            </h1>
            <p className="dt-muted">
              <Skeleton width={280} />
            </p>
          </div>
        </div>
        {children}
      </Loading>
    </CenteredPage>
  );
}

/**
 * A fresh load waits for the session before any page can render. Until then the router shows
 * this: the skeleton the deepest matching route declares as `handle.skeleton`.
 */
export function RouteSkeleton() {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const Page = (matches[i]!.handle as { skeleton?: ComponentType } | undefined)?.skeleton;
    if (Page) return <Page />;
  }
  return null;
}
